import { useFormik } from "formik";
import { ConfirmPopup } from "primereact/confirmpopup";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaEdit } from "react-icons/fa";
import { MdOutlineFolderOpen, MdOutlineSave } from "react-icons/md";
import * as Yup from "yup";

import { IDE_TYPE } from "../../../../../constants/common";
import Button from "../../../../Button";
import MyProjectsModal from "../../../../modals/common/MyProjectsModal";

const PlaygroundHeader = ({
  onGetPlaygroundProject,
  onCreateNewProject,
  onSaveProject,
  smoothy,
  isSaving,
}) => {
  const { t } = useTranslation();
  const defaultProjectName = t("untitled_project");

  const popupRef = useRef(null);
  const myProjectsModalRef = useRef();

  const [projectName, setProjectName] = useState(t("untitled_project"));
  const [renameVisible, setRenameVisible] = useState(false);

  const openMyProjectsModal = (projectData) => myProjectsModalRef.current?.openModal(projectData);

  const handleMyProjectsCreateSuccess = () => {
    onCreateNewProject();
    setProjectName(defaultProjectName);
    formik.setFieldValue("newProjectName", defaultProjectName);
  };

  const handleMyProjectsSuccess = (payload) => {
    onGetPlaygroundProject(payload);
    setProjectName(payload.project.name);
    formik.setFieldValue("newProjectName", payload.project.name);
  };

  const hasCodeContent = () => smoothy?.trim() !== "";

  const handleSaveProject = () => {
    onSaveProject(projectName);
  };

  useEffect(() => {
    if (!hasCodeContent()) return; // Don't save if empty

    const timeoutId = setTimeout(() => {
      onSaveProject(projectName, true);
    }, 500);

    return () => clearTimeout(timeoutId); // Clear previous debounce on new keystroke
  }, [smoothy, projectName]);

  const confirmRename = () => {
    setRenameVisible(true);
  };

  // Formik + Yup
  const formik = useFormik({
    initialValues: {
      newProjectName: projectName,
    },
    validationSchema: Yup.object({
      newProjectName: Yup.string()
        .min(2, t("validation.field_min_2"))
        .max(25, t("validation.field_max_25"))
        .required(t("validation.required")),
    }),
    onSubmit: (values) => {
      setProjectName(values.newProjectName.trim());
      setRenameVisible(false);
    },
    enableReinitialize: true,
  });

  const footer = (
    <div className="flex justify-end gap-2 px-5 pb-3">
      <Button
        variant="outlined"
        color="white"
        onClick={() => {
          formik.resetForm();
          setRenameVisible(false);
        }}
        label={t("cancel")}
      />
      <Button
        className="w-full"
        type="submit"
        onClick={formik.handleSubmit}
        disabled={!formik.isValid || formik.isSubmitting}
        label={t("rename")}
      />
    </div>
  );

  return (
    <div className="mb-2 flex items-center justify-between">
      <div className="flex items-center gap-2">
        <div className="flex cursor-pointer items-center space-x-2" onClick={confirmRename} ref={popupRef}>
          <h1 title={projectName} className="w-full max-w-[18ch] truncate text-2xl font-bold text-blue-500">
            {projectName}
          </h1>
          <FaEdit className="text-blue-500" />
        </div>
        <ConfirmPopup
          target={popupRef.current}
          visible={renameVisible}
          onHide={() => {
            setRenameVisible(false);
            formik.resetForm();
          }}
          message={
            <form onSubmit={formik.handleSubmit} className="flex flex-col gap-2">
              <input
                type="text"
                name="newProjectName"
                className={`w-full rounded border p-2 ${formik.errors.newProjectName && formik.touched.newProjectName ? "border-red-500" : "border-gray-300"}`}
                value={formik.values.newProjectName}
                maxLength={30}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {formik.errors.newProjectName && formik.touched.newProjectName && (
                <div className="text-sm text-red-500">{formik.errors.newProjectName}</div>
              )}
            </form>
          }
          footer={footer}
          pt={{
            message: "m-0",
          }}
        />
      </div>
      <div className="flex items-center space-x-2">
        <Button
          variant="text"
          iconLeft={<MdOutlineFolderOpen />}
          label={t("my_projects")}
          onClick={() => openMyProjectsModal({ IDEType: IDE_TYPE.SMOOTHY })}
        />
        <Button
          className="h-9 w-40"
          iconLeft={<MdOutlineSave />}
          label={t("save_project")}
          color="green"
          loading={isSaving}
          onClick={handleSaveProject}
          disabled={!hasCodeContent()}
        />
      </div>
      <MyProjectsModal
        ref={myProjectsModalRef}
        onSubmit={handleMyProjectsSuccess}
        onSubmitCreate={handleMyProjectsCreateSuccess}
      />
    </div>
  );
};

export default PlaygroundHeader;
